import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { authCallbackURL } from '../lib/authRedirect'
import { supabase } from '../lib/supabase'
import type { Profile } from '../lib/types'

type OAuthProvider = 'apple' | 'google' | 'azure'

interface AuthValue {
  session: Session | null
  user: User | null
  profile: Profile | null
  profileError: string | null
  loading: boolean
  signIn: (provider: OAuthProvider) => Promise<void>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<Profile | null>
}

const AuthContext = createContext<AuthValue | null>(null)

interface AccountContextRow {
  profile_id: string
  display_name: string | null
  phone_e164: string | null
  preferred_locale: string | null
  account_kind: Profile['default_account_kind']
  onboarding_completed_at: string | null
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const loadProfile = useCallback(async (userId: string) => {
    let lastError: unknown = null
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const { data, error } = await supabase.rpc('get_authenticated_account_context')
      if (!error) {
        const row = ((data as AccountContextRow[] | null) ?? [])[0] ?? null
        if (row && row.profile_id !== userId) throw new Error('The authenticated account context did not match the current session.')
        const next: Profile | null = row ? {
          id: row.profile_id,
          display_name: row.display_name,
          phone_e164: row.phone_e164,
          preferred_locale: row.preferred_locale ?? 'en',
          default_account_kind: row.account_kind,
          onboarding_completed_at: row.onboarding_completed_at,
        } : null
        setProfile(next)
        setProfileError(null)
        return next
      }
      lastError = error
      if (attempt < 2) await new Promise((resolve) => window.setTimeout(resolve, attempt === 0 ? 300 : 900))
    }
    setProfileError('Your existing account could not be loaded. Please try again.')
    throw lastError instanceof Error ? lastError : new Error('Account context unavailable')
  }, [])

  useEffect(() => {
    let active = true
    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return
      setSession(data.session)
      if (data.session?.user) {
        try {
          await loadProfile(data.session.user.id)
        } catch {
          // Keep the authenticated session out of onboarding. A failed account
          // lookup is recoverable and must never look like a new account.
        }
      }
      setLoading(false)
    })

    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession)
      if (!nextSession?.user) {
        setProfile(null)
        setProfileError(null)
        setLoading(false)
        return
      }
      window.setTimeout(() => {
        loadProfile(nextSession.user.id).finally(() => setLoading(false))
      }, 0)
    })

    return () => {
      active = false
      listener.subscription.unsubscribe()
    }
  }, [loadProfile])

  const value = useMemo<AuthValue>(() => ({
    session,
    user: session?.user ?? null,
    profile,
    profileError,
    loading,
    async signIn(provider) {
      const scopes = provider === 'azure' ? 'email openid profile' : undefined
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: authCallbackURL(),
          scopes,
        },
      })
      if (error) throw error
    },
    async signOut() {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      setProfile(null)
      setProfileError(null)
    },
    async refreshProfile() {
      if (!session?.user) return null
      return loadProfile(session.user.id)
    },
  }), [loadProfile, loading, profile, profileError, session])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside AuthProvider')
  return value
}
